import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home"; 
import OutlinedInput from "@mui/material/OutlinedInput";


const EmployeeModal = ({ open, onClose, onEmployeeEdit, initialData }) => {
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [formData, setFormData] = useState({
    firstName: initialData ? initialData.firstName : "",
    lastName: initialData ? initialData.lastName : "",
    contactNumber: initialData ? (initialData.contactNumber || "").replace(/^\+1/, "") : "",
    email: initialData ? initialData.email : "",
    empDesignation: initialData ? initialData.empDesignation : "",
    empAddress: initialData ? initialData.empAddress : "",
    empCity: initialData ? initialData.empCity : "",
    empState: initialData ? initialData.empState : "",
    empZipCode: initialData ? initialData.empZipCode : "",
    employeeId: initialData ? initialData.employeeId : null,
    companyId: initialData ? initialData.companyId: null,
    // contactNumber: initialData ? initialData.contactNumber: null,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (initialData) {
      setFormData((prevData) => ({
        ...prevData,
        firstName: initialData.firstName || prevData.firstName,
        lastName: initialData.lastName || prevData.lastName,
        contactNumber: (initialData.contactNumber || prevData.contactNumber || "").replace(/^\+1/, ""),
        email: initialData.email || prevData.email,
        empDesignation: initialData.empDesignation || prevData.empDesignation,
        empAddress: initialData.empAddress || prevData.empAddress,
        empCity: initialData.empCity || prevData.empCity,
        empState: initialData.empState || prevData.empState,
        empZipCode: initialData.empZipCode || prevData.empZipCode,
        employeeId: initialData.employeeId || prevData.employeeId,
        companyId: initialData.companyId || prevData.companyId,
      }));
    }
  }, [initialData]); 
  

  useEffect(() => {
    const isValidEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email.trim());
    const isValidPhone = /^\d{10}$/.test(formData.contactNumber.trim());
    const isValidZip = /^\d{5}$/.test(formData.empZipCode.trim());
    const isValidCity = /^[A-Za-z\s]+$/.test(formData.empCity.trim());
    const isValidState = /^[A-Za-z\s]+$/.test(formData.empState.trim());

    const isValid =
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      isValidPhone &&
      isValidEmail &&
      formData.empDesignation.trim() &&
      formData.empAddress.trim() &&
      isValidCity &&
      isValidState &&
      isValidZip;

    setSaveButtonDisabled(!isValid);
  },[formData]);

  useEffect(() => {
    setSaveButtonDisabled(false);
  }, []);

  const handleSave = () => {
    if (!saveButtonDisabled) {
      const payload = {
        ...initialData,
        ...formData, 
      };
      onEmployeeEdit(payload); 
      onClose(); 
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
        {initialData ? "Edit Employee Details" : "Add Employee"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ padding: "20px", textAlign: "center" }}>
          <form>
            <Grid container spacing={2}>
              {/* First Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Only letters and spaces allowed."
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Only letters and spaces allowed."
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="contactNumber"
                  required
                  value={formData.contactNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  error={formData.contactNumber.length > 0 && formData.contactNumber.length !== 10}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="10-digit number only."
                />
              </Grid>

              {/* Email Address */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z0-9@.]*$/.test(value)) { 
                        handleChange(e);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value)) {
                        console.error("Invalid email format");
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  error={formData.email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email)}
                  helperText={
                      formData.email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email)
                          ? "Please enter a valid email address."
                          : "You will receive an email after successful update."
                  }
                />
              </Grid>

              {/* Designation */}
              <Grid item xs={12}>
              <FormControl fullWidth required variant="outlined">
                <InputLabel id="designation-label">Designation</InputLabel>
                <Select
                  labelId="designation-label"
                  id="designation"
                  name="empDesignation"
                  value={formData.empDesignation}
                  onChange={handleChange}
                  input={<OutlinedInput label="Designation" />}
                  startAdornment={
                    <InputAdornment position="start">
                      <WorkIcon />
                    </InputAdornment>
                  } 
                >
                  <MenuItem value="Owner">Owner</MenuItem>
                  <MenuItem value="Manager">Manager</MenuItem>
                  <MenuItem value="Technician">Technician</MenuItem>
                  <MenuItem value="Partner">Partner</MenuItem>
                </Select>
              </FormControl>
            </Grid>


              {/* Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="empAddress"
                  required
                  value={formData.empAddress}
                  onChange={handleChange}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><HomeIcon /></InputAdornment>) }} 
                />
              </Grid>

              {/* City & State */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="empCity"
                  required
                  value={formData.empCity}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><LocationOnIcon /></InputAdornment>) }} 
                  helperText="Only letters and spaces allowed."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  name="empState"
                  required
                  value={formData.empState}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><LocationOnIcon /></InputAdornment>) }} 
                  helperText="Only letters and spaces allowed."
                />
              </Grid>

              {/* Zip Code */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  name="empZipCode"
                  required
                  value={formData.empZipCode}
                  error={formData.empZipCode.length > 0 && formData.empZipCode.length !== 5}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,5}$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="5-digit number only."
                />
              </Grid>
            </Grid>

            {/* Buttons */}
            <Button
              variant="contained"
              sx={{ marginTop: "20px", width: "100%", padding: "12px", fontWeight: "bold", backgroundColor: "#002244", color: "white" }}
              onClick={handleSave}
              disabled = {saveButtonDisabled}
            >
              Save Employee
            </Button>
            <Button variant="outlined" sx={{ marginTop: "10px", width: "100%", fontWeight: "bold" }} onClick={onClose}>
              Cancel
            </Button>
          </form>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeModal;